export type ErrorDetails = {
  errorCode?: string;
  errorMessage?: string;
};

const normalize = (code: unknown, message: unknown): ErrorDetails => {
  const normalizedMessage = typeof message === 'string' ? message.trim() : '';

  return {
    errorCode: typeof code === 'string' ? code : undefined,
    errorMessage: normalizedMessage || undefined,
  };
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const getErrorDetailsFromObject = (error: unknown): ErrorDetails => {
  if (!isPlainObject(error)) {
    return {};
  }

  const topLevel = normalize(error.errorCode, error.message);
  if (topLevel.errorCode || topLevel.errorMessage) {
    return topLevel;
  }

  // Growth wraps failures in shared-utils' `ApiResponse`, which nests them one level
  // down and names the code `errorType` — that is what the toast map is keyed on.
  const envelope = error.error;
  if (!isPlainObject(envelope)) {
    return topLevel;
  }

  return normalize(envelope.errorType, envelope.message);
};

export const extractErrorDetails = (error: unknown): ErrorDetails => {
  const details = getErrorDetailsFromObject(error);
  if (details.errorCode || details.errorMessage) return details;

  if (typeof error !== 'string') return {};

  try {
    const parsedError = JSON.parse(error);
    return getErrorDetailsFromObject(parsedError);
  } catch {
    return {};
  }
};
